/**
 * Earned Wage Access self-service card (P07) — employee portal.
 *
 * Shows the employee's available-to-draw amount, lets them request a draw, and
 * lists their recent draw history. Backed by the employee-scoped /hr/ewa/*
 * endpoints.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SavingsOutlined from '@mui/icons-material/SavingsOutlined';
import { ewaApi } from '@/api/endpoints/ewa';
import { useNotificationStore } from '@/stores/notificationStore';
import { useJurisdiction } from '@/hooks/useJurisdiction';

const STATUS_COLOR: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  approved: 'success',
  disbursed: 'success',
  pending: 'warning',
  rejected: 'error',
};

export default function EwaCard() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotificationStore();
  const { currencySymbol } = useJurisdiction();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');

  const { data: eligible } = useQuery({
    queryKey: ['ewa/eligible'],
    queryFn: ewaApi.eligible,
  });
  const { data: draws } = useQuery({ queryKey: ['ewa/my-draws'], queryFn: ewaApi.myDraws });

  const requestMut = useMutation({
    mutationFn: (amt: number) => ewaApi.requestDraw(amt),
    onSuccess: () => {
      showSuccess('Draw request submitted');
      setOpen(false);
      setAmount('');
      queryClient.invalidateQueries({ queryKey: ['ewa/eligible'] });
      queryClient.invalidateQueries({ queryKey: ['ewa/my-draws'] });
    },
    onError: (e: { response?: { data?: { detail?: string } } }) =>
      showError(e?.response?.data?.detail || 'Draw request failed'),
  });

  const available = Number(eligible?.available_to_draw ?? 0);
  const money = (v: string | number) =>
    `${currencySymbol}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
          <SavingsOutlined color="primary" />
          <Typography variant="subtitle1" fontWeight={700}>
            Earned Wage Access
          </Typography>
        </Stack>

        <Stack direction="row" spacing={3} flexWrap="wrap" mb={1.5}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Available to draw now
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {eligible ? money(eligible.available_to_draw) : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Earned to date
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {eligible ? money(eligible.earned_to_date) : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Already drawn (this period)
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {eligible ? money(eligible.already_drawn) : '—'}
            </Typography>
          </Box>
        </Stack>

        <Button
          variant="contained"
          size="small"
          disabled={available <= 0}
          onClick={() => setOpen(true)}
        >
          Request a draw
        </Button>

        {draws && draws.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="overline" color="text.secondary">
              Recent draws
            </Typography>
            <Stack spacing={0.5} mt={0.5}>
              {draws.slice(0, 5).map((d) => (
                <Stack key={d.id} direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">
                    {money(d.draw_amount)}{' '}
                    <Typography component="span" variant="caption" color="text.secondary">
                      · {String(d.period_month).padStart(2, '0')}/{d.period_year}
                    </Typography>
                  </Typography>
                  <Chip size="small" label={d.status} color={STATUS_COLOR[d.status] || 'default'} />
                </Stack>
              ))}
            </Stack>
          </>
        )}
      </CardContent>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Request a draw</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Available now: <strong>{money(available)}</strong>
          </Typography>
          <TextField
            label="Amount"
            type="number"
            fullWidth
            autoFocus
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 0, max: available, step: '0.01' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              requestMut.isPending ||
              !amount ||
              Number(amount) <= 0 ||
              Number(amount) > available
            }
            onClick={() => requestMut.mutate(Number(amount))}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
